import { useCallback } from "react";
import { useSupabaseClient } from "@/lib/supabase";
import { useProfileId } from "@/features/onboarding/hooks/useProfileId";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { NotificationType } from "../types";

export interface CreateNotificationInput {
  type: NotificationType;
  title: string;
  body?: string;
  href?: string;
  metadata?: Record<string, unknown>;
}

function db(client: SupabaseClient) {
  return (client as SupabaseClient<Record<string, never>>).from("notifications");
}

/**
 * Returns a stable function that inserts a notification for the current user.
 * For events not covered by DB triggers
 * (e.g. persona_created, decodificacao_done, roteiro_generated).
 *
 * `user_id` is required by RLS (`auth.uid() = user_id`). It's the profile
 * UUID, not the Clerk id — resolved via `useProfileId()`.
 */
export function useCreateNotification() {
  const { client, isReady } = useSupabaseClient();
  const { profileId } = useProfileId();

  return useCallback(
    async (input: CreateNotificationInput): Promise<void> => {
      if (!isReady || !profileId) return;
      const { error } = await db(client).insert({ ...input, user_id: profileId });
      if (error) console.error("[notifications] insert error:", error);
    },
    [client, isReady, profileId],
  );
}
